import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiX, FiSearch } from 'react-icons/fi';

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  getLabel,
  placeholder = 'Select...',
  isInvalid = false,
  disabled = false,
  allowClear = true,
  optionKey = 'id',
  labelKey = 'name',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  const normalized = (options || []).map((o) => {
    if (o && typeof o === 'object') {
      const v = 'value' in o ? o.value : o[optionKey];
      const l = 'label' in o ? o.label : getLabel ? getLabel(o) : o[labelKey];
      const d = 'depth' in o ? o.depth : 0;
      return { value: v, label: l, depth: d, search: String(l == null ? '' : l).toLowerCase() };
    }
    return { value: o, label: o, depth: 0, search: String(o).toLowerCase() };
  });

  const selected = normalized.find((o) => String(o.value) === String(value));
  const filtered = query.trim()
    ? normalized.filter((o) => o.search.includes(query.trim().toLowerCase()))
    : normalized;
  const visibleOptions = filtered.slice(0, 200);

  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) setActiveIdx(-1);
  }, [open]);

  useEffect(() => {
    if (open) {
      positionMenu();
      const reposition = () => positionMenu();
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);
      return () => {
        window.removeEventListener('scroll', reposition, true);
        window.removeEventListener('resize', reposition);
      };
    }
  }, [open]);

  const positionMenu = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = 300;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < menuHeight && rect.top > menuHeight
      ? rect.top - menuHeight - 6
      : rect.bottom + 4;
    setMenuStyle({
      position: 'fixed',
      top: Math.max(4, top),
      left: rect.left,
      width: Math.max(220, rect.width),
      zIndex: 9999,
    });
  };

  const pick = (o) => {
    onChange(o.value);
    setOpen(false);
    setQuery('');
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && filtered[activeIdx]) pick(filtered[activeIdx]);
    }
  };

  return (
    <div className={`searchable-select ${open ? 'open' : ''} ${isInvalid ? 'is-invalid' : ''}`} ref={rootRef}>
      <div
        ref={triggerRef}
        className={`searchable-select-trigger ${disabled ? 'disabled' : ''}`}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setQuery('');
        }}
      >
        {selected ? (
          <span className="searchable-select-value" title={String(selected.label)}>
            {selected.label}
          </span>
        ) : (
          <span className="searchable-select-placeholder">{placeholder}</span>
        )}
        {selected && allowClear && !disabled && (
          <button type="button" className="searchable-select-clear" onClick={clear} tabIndex={-1}><FiX /></button>
        )}
        <FiChevronDown className={`searchable-select-arrow ${open ? 'open' : ''}`} />
      </div>

      {open && !disabled && (
        <div className="searchable-select-menu" style={menuStyle}>
          <div className="searchable-select-search">
            <FiSearch className="searchable-select-search-icon" />
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(-1);
              }}
              onKeyDown={onKeyDown}
              placeholder="Type to search..."
            />
          </div>
          <div className="searchable-select-list">
            {filtered.length === 0 ? (
              <div className="searchable-select-empty">No options found</div>
            ) : (
              visibleOptions.map((o, i) => (
                <div
                  key={`${o.value}-${i}`}
                  className={`searchable-select-option ${i === activeIdx ? 'active' : ''} ${
                    String(o.value) === String(value) ? 'selected' : ''
                  }`}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => pick(o)}
                  style={{
                    paddingLeft: 12 + (o.depth || 0) * 16,
                    fontWeight: (o.depth || 0) <= 1 ? 600 : 400,
                    fontSize: (o.depth || 0) === 0 ? 13 : 12,
                  }}
                >
                  {o.depth > 0 && (
                    <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>
                      {'└ '}
                    </span>
                  )}
                  {o.label}
                </div>
              ))
            )}
            {filtered.length > visibleOptions.length && (
              <div className="searchable-select-empty">+{filtered.length - visibleOptions.length} more — type to search</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}